import { Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { FormControlLabel, Grid, Switch, Skeleton } from "@mui/material";
import { MapContainer, ScaleControl, Pane as LeafletPane } from "react-leaflet";
import { useTranslation } from "react-i18next";

import { CalculationResult, Selection } from "src/types";
import { useBounds } from "src/hooks";
import { useCase } from "src/hooks/useCase";
import { useCalculationResults } from "src/hooks/results";
import { CaseLayout } from "src/layouts/CaseLayout";
import { MapZoomControls } from "src/components/map/MapZoomControls";
import { MapSizeObserver } from "src/components/map/MapSizeObserver";
import { Pane } from "src/components/Pane";
import { CalculationResultOnMap } from "src/components/CalculationResultOnMap";
import { CaseResultSelection } from "src/components/CaseResultSelection";
import { CaseResultPoisOnMap } from "src/components/on-map/CaseResultPoisOnMap";
import { DownloadCaseResultButton } from "src/components/DownloadCaseResultButton";
import { CoordinatesControl } from "src/components/map/CoordinatesControl";
import { CaseStatusButton } from "src/components/CaseStatusButton";
import { MapControlBase } from "src/components/map/MapControlBase";
import { BaselayerOnMap } from "src/components/on-map/BaselayerOnMap";
import { ActiveOverlayersOnMap } from "src/components/on-map/ActiveOverlayersOnMap";
import { OverlayersLegend } from "src/components/OverlayersLegend";
import { LayerManagerOnMap } from "src/components/LayerManager/LayerManagerOnMap";
import { CalculationType } from "src/components/map/CalculationType";
import { useSetAtom } from "jotai";
import { selectedScenarioIdAtom } from "src/atoms/overlayers";
import { SearchControl } from "src/components/map/SearchControl";

export function CaseResultsPage() {
  const { t } = useTranslation();
  const { caseId = "" } = useParams<{ caseId: string }>();
  const bounds = useBounds(caseId);
  const setScenarioId = useSetAtom(selectedScenarioIdAtom);

  const { data: acase } = useCase(caseId);
  const { data: calculationResults } = useCalculationResults(caseId);

  const [showPois, setShowPois] = useState(true);
  const [activeCalculationResult, setActiveCalculationResult] = useState<CalculationResult | null>(null);
  const [selection, setSelection] = useState<Selection>({
    scenario: null,
    substance: null,
    calculation: null,
  });

  /**
   * Effects
   */
  useEffect(setInitialSelection, [calculationResults, setScenarioId]);
  useEffect(updateActiveCalculationResult, [calculationResults, selection]);

  /**
   * Functions
   */

  function setInitialSelection() {
    if (!calculationResults) return;
    if (calculationResults.length === 0) return;
    setScenarioId(calculationResults[0].scenarioId.toString());
    setSelection({
      scenario: calculationResults[0].scenarioId.toString(),
      substance: calculationResults[0].substanceId.toString(),
      calculation: calculationResults[0].mapTypeKey,
    });
  }
  function updateActiveCalculationResult() {
    if (!calculationResults) return;

    const result = getActiveCalculationResult(calculationResults, selection);
    if (result) setActiveCalculationResult(result);
  }

  function handleSelectionChange(newSelection: Selection) {
    setScenarioId(newSelection.scenario?.toString());
    setSelection(newSelection);
  }

  // render the component
  return render();

  function render() {
    if (!acase) return null;
    if (!bounds) return null;

    return (
      <CaseLayout toolbarChildren={<CaseStatusButton caseId={caseId} />}>
        <Styles>
          {renderSelectionPane()}

          <MapContainer bounds={bounds} zoomControl={false} className="map-container">
            <SearchControl position="topleft" />
            <LeafletPane name="drawing-pane" style={{ zIndex: 9998 }} />
            <LeafletPane name="drawing-pane-vertex" style={{ zIndex: 9999 }} />

            <MapControlBase position="bottomleft">
              {activeCalculationResult && (
                <Suspense fallback={null}>
                  <CalculationResultOnMap calculationResult={activeCalculationResult} />
                </Suspense>
              )}
            </MapControlBase>
            <ScaleControl position="bottomleft" imperial={false} />
            {activeCalculationResult && (
              <MapControlBase position="topleft">
                <CalculationType calculationResult={activeCalculationResult} />
              </MapControlBase>
            )}

            <MapControlBase position="bottomleft">
              <CoordinatesControl />
            </MapControlBase>
            <MapControlBase position="bottomleft">
              <OverlayersLegend context="case-results" />
            </MapControlBase>

            <MapSizeObserver />
            <MapZoomControls position="bottomright" />

            <LayerManagerOnMap context="case-results" />

            {calculationResults && (
              <Suspense fallback={null}>
                <CaseResultPoisOnMap
                  ospmEnabled={acase.ospmEnabled}
                  caseId={caseId}
                  active={showPois}
                  calculationResults={calculationResults}
                  selection={selection}
                />
              </Suspense>
            )}

            <BaselayerOnMap />
            <ActiveOverlayersOnMap context="case-results" />
          </MapContainer>
        </Styles>
      </CaseLayout>
    );
  }

  function renderSelectionPane() {
    return (
      <Pane>
        <CaseResultSelection caseId={caseId} selection={selection} onSelectionChange={handleSelectionChange} />

        {/* Show POIs toggle */}
        <Grid container spacing={3} sx={{ paddingTop: 3 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={showPois} onChange={(_event, checked) => setShowPois(checked)} color="primary" />
              }
              label={t<string>("label.showPointsOfInterest")}
            />
          </Grid>

          <Suspense fallback={<Skeleton variant="rectangular" width={211} height={34} />}>
            <Grid item xs={12}>
              <DownloadCaseResultButton caseId={caseId} />
            </Grid>
          </Suspense>
        </Grid>
      </Pane>
    );
  }
}

function getActiveCalculationResult(calculationResults: CalculationResult[], selection: Selection) {
  if (!calculationResults) return;

  return calculationResults.find((calculationResult) => {
    return (
      selection.scenario === calculationResult.scenarioId.toString() &&
      selection.substance === calculationResult.substanceId.toString() &&
      selection.calculation === calculationResult.mapTypeKey
    );
  });
}

const Styles = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: auto;

  .map-container,
  .leaflet-container {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }
`;
